import { useEffect, useState } from 'react';
import type { MyFeedbackItem } from '@/lib/apiClient';
import { snippet } from '@/lib/everyonesFeedback';
import { CATEGORY_LABEL, firstLine, fmtWhen, myStatusLabel } from '@/lib/feedbackMine';
import StopFollowingControl from './StopFollowingControl';

/**
 * Its own `fbm-` styles, declared here and not borrowed from the drawer's `fb-` ones: a class in this app is live only while the
 * component that declares it is mounted (there are no CSS Modules), so a list that leaned on its neighbour's rules would be
 * styled until somebody moved it.
 */
const css = `
  .fbm-list { display: flex; flex-direction: column; gap: 8px; padding: 14px 18px 18px; }
  .fbm-note { margin: 0; padding: 18px; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-n-500); }
  .fbm-err { margin: 0; padding: 18px; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-danger); }
  .fbm-item { border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); background: #fff; }
  .fbm-item.unread { border-color: var(--ew-green); }
  .fbm-row {
    display: flex; flex-direction: column; gap: 4px; align-items: stretch; width: 100%; text-align: left;
    padding: 10px 12px; background: transparent; border: 0; cursor: pointer; color: var(--ew-dark);
    font: var(--ew-fw-regular) 13px/1.35 var(--ew-font);
  }
  .fbm-meta { display: flex; align-items: center; gap: 8px; font: var(--ew-fw-medium) 11px/1 var(--ew-font); color: var(--ew-n-500); }
  .fbm-status { padding: 2px 8px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); color: var(--ew-n-600); }
  .fbm-status.done { background: var(--ew-green-50, #E6F2EB); color: var(--ew-green); }
  .fbm-new { padding: 2px 8px; border-radius: var(--ew-r-pill); background: var(--ew-green); color: #fff; }
  .fbm-spacer { flex: 1; }
  .fbm-first { font-weight: var(--ew-fw-medium); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .fbm-detail { padding: 0 12px 12px; display: flex; flex-direction: column; gap: 10px; border-top: 1px solid var(--ew-n-100); }
  .fbm-msg { margin: 10px 0 0; white-space: pre-wrap; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbm-reply { padding: 8px 10px; border-radius: var(--ew-r-sm); background: var(--ew-n-50); border-left: 3px solid var(--ew-green); }
  .fbm-reply-head { font: var(--ew-fw-medium) 11.5px/1.2 var(--ew-font); color: var(--ew-n-500); margin-bottom: 4px; }
  .fbm-reply-head b { color: var(--ew-dark); }
  .fbm-reply p { margin: 0; white-space: pre-wrap; font: var(--ew-fw-regular) 13px/1.45 var(--ew-font); color: var(--ew-dark); }
  .fbm-muted { margin: 0; font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .fbm-why { margin: 0; padding: 8px 10px; border-radius: var(--ew-r-sm); background: var(--ew-n-50); border-left: 3px solid var(--ew-n-300, #C9CDD1); display: flex; flex-direction: column; gap: 8px; align-items: flex-start; }
  .fbm-why p { margin: 0; font: var(--ew-fw-regular) 12.5px/1.45 var(--ew-font); color: var(--ew-dark); }
`;

/** What the list needs to let somebody stop following an item: which are on their way, why the last one failed, and the action. */
export interface StopHandlers {
  /** Ids with a stop on its way. */
  pending: ReadonlySet<number>;
  /** Why the last attempt on an id failed, in words for a person. */
  errors: ReadonlyMap<number, string>;
  onStop: (id: number) => void;
  /** Somebody starts over on an id, or backs out of it: what its last attempt said is forgotten. */
  onReset?: (id: number) => void;
}

interface Props {
  items: MyFeedbackItem[] | undefined;
  isLoading: boolean;
  /** The request failed. Never shown as "nothing here": an outage is not an empty list. */
  isError: boolean;
  /** Called when an item that has something unread is open on screen. */
  onSeen: (id: number) => void;
  /** "Stop following", for the items the API says may be stopped. Without it nothing is offered. */
  stop?: StopHandlers;
  /** What has just happened to the list, for a screen reader: said in ONE live region that is always here (see `styled`). */
  announcement?: string;
}

/**
 * The items I follow, newest activity first. Opening one shows what I sent and everything said to me about it, and marks it read.
 *
 * ⚠️ A FAILED LOAD IS NOT AN EMPTY LIST. React Query leaves `data` undefined once its retries are spent, and `data ?? []` would
 * tell somebody waiting on an answer that they have sent nothing.
 */
export default function MyFeedback({ items, isLoading, isError, onSeen, stop, announcement = '' }: Props) {
  const [openId, setOpenId] = useState<number | null>(null);
  // The item "Show that request" was pressed for: opened, and focus moved to its row, once it is on screen. A button that leaves the page (this card
  // closes as the other opens) takes focus with it, so the move is made on purpose.
  const [jumpTo, setJumpTo] = useState<number | null>(null);
  const open = items?.find((i) => i.id === openId);
  const openUnread = open?.unread ?? false;
  useEffect(() => {
    if (jumpTo == null) return;
    document.getElementById(`fbm-row-${jumpTo}`)?.focus();
    setJumpTo(null);
  }, [jumpTo]);

  // An item that leaves the list takes "open" with it. Stopping following is a normal way for that to happen, and a stale id would open the
  // item again, already expanded and marked read without the person having touched it, if it came back (a refetch that began before the
  // stop and lands after it). Only judged on a list that is HERE: while it is loading, or has failed, nothing is known about what is open.
  useEffect(() => {
    if (openId != null && items && !items.some((i) => i.id === openId)) setOpenId(null);
  }, [openId, items]);

  // Whatever is on screen is being read. Keyed on the unread flag too, so a reply that lands while the item is open is marked
  // read as well; and a call that fails (the read-only "view as" mode refuses it) does not repeat, because nothing it depends on
  // changes.
  useEffect(() => {
    if (openId != null && openUnread) onSeen(openId);
  }, [openId, openUnread, onSeen]);

  // One <style>, rendered with whichever state is showing. ⚠️ AND ONE LIVE REGION, always in the page and always in the same place: a status
  // element that arrives together with its words is unreliably spoken, and "you stopped following that" is said as the item it was about
  // leaves the list (which may be the last one, and the list then becomes the empty note).
  const styled = (body: React.ReactNode) => (<><style>{css}</style><span className="ew-sr-only" role="status">{announcement}</span>{body}</>);

  if (isLoading) return styled(<p className="fbm-note">Loading…</p>);
  if (isError || !items) {
    return styled(<p className="fbm-err" role="alert">We couldn’t load your feedback just now. Try again in a moment.</p>);
  }
  if (items.length === 0) {
    return styled(<p className="fbm-note">Nothing here yet. Feedback you send, and anything you say “me too” to, shows up here, with any reply.</p>);
  }

  return styled(
    <div className="fbm-list">
      {items.map((i) => {
        const isOpen = i.id === openId;
        const label = myStatusLabel(i);
        // The request this one was combined into, when it is in THIS list (I follow it: everybody who followed this one was added to it), and only
        // for the row that is open, which is the only one that says. Not found is not "I stopped following it": the list holds a limited number of
        // items, so nothing is said about that.
        const target = isOpen && i.combinedInto != null ? items.find((o) => o.id === i.combinedInto) : undefined;
        return (
          <div key={i.id} className={`fbm-item${i.unread ? ' unread' : ''}`}>
            <button
              type="button"
              id={`fbm-row-${i.id}`}
              className="fbm-row"
              aria-expanded={isOpen}
              onClick={() => setOpenId(isOpen ? null : i.id)}
            >
              <span className="fbm-meta">
                <span>{CATEGORY_LABEL[i.category] ?? i.category}</span>
                <span className={`fbm-status${label === 'Done' ? ' done' : ''}`}>{label}</span>
                <span className="fbm-spacer" />
                {i.unread && <span className="fbm-new">New</span>}
                <span>{fmtWhen(i.createdAt)}</span>
              </span>
              <span className="fbm-first">{firstLine(i.message)}</span>
            </button>

            {isOpen && (
              <div className="fbm-detail">
                <p className="fbm-msg">{i.message}</p>
                {/* ⚠️ WHY SOMEBODY FOLLOWS WHAT THEY NEVER ASKED FOR: an admin combined a request they follow into this one. Never said of the sender. */}
                {i.addedByMerge === true && (
                  <div className="fbm-why">
                    <p>A request you follow was combined into this one, so you follow it too.{i.status === 'open' ? ' You’ll be told when it’s answered.' : ''}</p>
                  </div>
                )}
                {/* ⚠️ AND WHAT BECAME OF THIS ONE, in words that are not "Closed": an admin found it was the same as another request. */}
                {i.combinedInto != null && (
                  <div className="fbm-why">
                    <p>
                      An admin found that this asks for the same thing as another request, and combined them. Everyone who followed this one was added to
                      that one.{target ? ` You follow it too${target.status === 'open' ? ', so you’ll be told when it’s answered' : ''}.` : ''}
                    </p>
                    {target && (
                      <button
                        type="button" className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: 12 }}
                        aria-label={`Show the request this was combined with: ${firstLine(target.message, 50)}`}
                        onClick={() => { setOpenId(target.id); setJumpTo(target.id); }}
                      >
                        Show that request
                      </button>
                    )}
                  </div>
                )}
                {i.replies.map((r) => (
                  <div key={r.id} className="fbm-reply">
                    <div className="fbm-reply-head"><b>{r.authorName ?? 'The team'}</b> · {fmtWhen(r.createdAt)}</div>
                    <p>{r.body}</p>
                  </div>
                ))}
                {i.replies.length === 0 && i.status === 'open' && (
                  <p className="fbm-muted">No reply yet. It will appear here when there is one.</p>
                )}
                {i.status === 'closed' && (
                  <p className="fbm-muted">
                    {label === 'Done' ? 'Marked done' : label === "Won't do" ? 'Closed as won’t do' : label === 'Combined' ? 'Combined with another request' : 'Closed'}
                    {i.closedAt ? ` · ${fmtWhen(i.closedAt)}` : ''}
                  </p>
                )}
                {/* Only where the API says this follow can be stopped: a "me too", never the follow that comes with sending an item. */}
                {i.canStopFollowing === true && stop && (
                  <StopFollowingControl
                    about={snippet(i.message, 50)}
                    pending={stop.pending.has(i.id)}
                    error={stop.errors.get(i.id) ?? null}
                    onStop={() => stop.onStop(i.id)}
                    onReset={() => stop.onReset?.(i.id)}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>,
  );
}
